// Read helper for the /client/billing tab. Pulls every approved-or-later
// case the signed-in client owns, attaches the active (non-replaced)
// quote, and sorts by urgency. Pure read — no mutations from this path.
//
// The page treats `quotes.status='replaced'` as "superseded" — when an
// admin re-approves a case with a new tier, the old quote row stays
// around but flips to `replaced`. Only the active quote is shown.

import { supabase } from "./supabase";
import type { ContractRow, ContractStatus } from "./caseApprovals";
import type { CompetitionStatus, CompetitionTier, EngagementModel, Quote } from "../types/database";

const BILLING_STATUSES: CompetitionStatus[] = [
  "approved",
  "contract_pending",
  "contract_signed",
  "payment_pending",
  "final_invoice_pending",
  "final_invoice_paid",
];

// Most-urgent-first ordering. Anything not listed falls through to the
// end (shouldn't happen because BILLING_STATUSES bounds the query, but
// the fallback keeps the sort total).
const STATUS_PRIORITY: Record<string, number> = {
  payment_pending: 0,
  contract_pending: 1,
  approved: 2,
  contract_signed: 3,
  final_invoice_pending: 4,
  final_invoice_paid: 5,
};

export type BillingQuote = {
  id: string;
  scholarship_amount_cents: number;
  manual_discount_percent: number;
  per_student_rate_cents: number;
  per_submission_rate_cents: number;
  per_team_presentation_rate_cents: number | null;
  flat_rm_fee_cents: number | null;
  initial_payment_cents: number;
  notes_for_client: string | null;
  generated_at: string;
  /** Set when the quote was approved with a promo code. */
  promo_code?: string | null;
  promo_discount_cents?: number;
};

export type BillingContract = {
  id: string;
  docusign_envelope_id: string;
  status: ContractStatus;
  sent_at: string;
  viewed_at: string | null;
  signed_at: string | null;
  declined_at: string | null;
  declined_reason: string | null;
  voided_at: string | null;
};

export type BillingCase = {
  competition_id: string;
  case_name: string;
  status: CompetitionStatus;
  engagement_model: EngagementModel;
  tier: CompetitionTier | null;
  /** Convenience mirror of `contract.signed_at` for the (null contract) case. */
  contract_signed_at: string | null;
  /** Convenience mirror of `payment.paid_at` (or the legacy field) for cases
   *  that have settled their initial payment. */
  payment_paid_at: string | null;
  quote: BillingQuote | null;
  /** Latest contract row for the case, or null if none yet. */
  contract: BillingContract | null;
  /** Latest INITIAL invoice (kind='initial' — Stripe Checkout). Null until
   *  the docusign-webhook fires create-stripe-checkout. */
  payment: BillingInitialPayment | null;
  /** Latest closeout invoice (kind='final'). Null until the admin
   *  finalizes the case via the Complete-case workflow. */
  final_invoice: BillingFinalInvoice | null;
};

export type BillingInitialPayment = {
  id: string;
  status: "pending" | "paid" | "failed" | "expired" | "refunded" | "voided";
  amount_cents: number;
  tax_cents: number;
  total_cents: number;
  paid_at: string | null;
  failed_at: string | null;
  failure_reason: string | null;
  /** Stripe Checkout Session URL — populated while status='pending' or
   *  'failed' (so retries route back to the same session if Stripe lets us).
   *  Null once the session is consumed. */
  checkout_url: string | null;
  receipt_url: string | null;
  expires_at: string | null;
};

export type BillingFinalInvoice = {
  id: string;
  status: "pending" | "paid" | "failed" | "voided";
  amount_cents: number;
  tax_cents: number;
  total_cents: number;
  due_date: string | null;
  paid_at: string | null;
  failed_at: string | null;
  voided_at: string | null;
  failure_reason: string | null;
  void_reason: string | null;
  stripe_invoice_hosted_url: string | null;
  stripe_invoice_pdf_url: string | null;
  stripe_receipt_url: string | null;
};

function quoteToBillingQuote(q: Quote, promoCode?: string | null): BillingQuote {
  return {
    id: q.id,
    scholarship_amount_cents: q.scholarship_amount_cents,
    manual_discount_percent: q.manual_discount_percent,
    per_student_rate_cents: q.per_student_rate_cents,
    per_submission_rate_cents: q.per_submission_rate_cents,
    per_team_presentation_rate_cents: q.per_presentation_rate_cents,
    flat_rm_fee_cents: q.flat_rm_fee_cents,
    initial_payment_cents: q.initial_payment_cents,
    notes_for_client: q.notes_for_client,
    generated_at: q.generated_at,
    promo_code: promoCode ?? null,
    promo_discount_cents: typeof q.promo_discount_cents === "number" ? q.promo_discount_cents : 0,
  };
}

export async function listBillingForClient(): Promise<BillingCase[]> {
  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError || !userData.user) return [];
  const uid = userData.user.id;

  const { data, error } = await supabase
    .from("competitions")
    .select("id, name, status, engagement_model, tier")
    .eq("created_by", uid)
    .in("status", BILLING_STATUSES);
  if (error || !data) return [];

  const competitionIds = data.map((r) => r.id as string);
  if (competitionIds.length === 0) return [];

  // Fetch quote + contract rows in parallel. For each:
  //   - quotes: most-recent non-replaced row per competition.
  //   - contracts: most-recent row per competition (any status — voided
  //     and declined rows still get surfaced in the UI so the client
  //     understands what's happening).
  // Both queries return all rows; we collapse in JS via Maps.
  const [quoteRes, contractRes] = await Promise.all([
    supabase
      .from("quotes")
      .select("*")
      .in("competition_id", competitionIds)
      .neq("status", "replaced")
      .order("generated_at", { ascending: false }),
    supabase
      .from("contracts")
      .select(
        "id, competition_id, docusign_envelope_id, status, sent_at, viewed_at, signed_at, declined_at, declined_reason, voided_at, created_at",
      )
      .in("competition_id", competitionIds)
      .order("created_at", { ascending: false }),
  ]);
  if (quoteRes.error) return [];
  // Distinguish "no contract row yet" (empty data, expected before a contract
  // is sent) from a genuine query failure. Now that authenticated has SELECT on
  // contracts, an error here is a real problem (RLS/permission/schema) — log it
  // loudly rather than silently rendering "no contract". We still degrade
  // gracefully (don't blank the whole billing page) but the error is no longer
  // swallowed.
  if (contractRes.error) {
    console.error("[clientBilling] contracts lookup failed:", contractRes.error.message);
  }

  const latestQuoteByCompetition = new Map<string, Quote>();
  for (const row of (quoteRes.data ?? []) as Quote[]) {
    if (!latestQuoteByCompetition.has(row.competition_id)) {
      latestQuoteByCompetition.set(row.competition_id, row);
    }
  }

  // Hydrate the promo code text from applied_promo_code_id so the
  // billing tab can render "Promo: EARLYBIRD" rather than just the
  // dollar discount.
  const promoIds = Array.from(latestQuoteByCompetition.values())
    .map((q) => q.applied_promo_code_id)
    .filter((v): v is string => typeof v === "string" && v.length > 0);
  const promoCodeById = new Map<string, string>();
  if (promoIds.length > 0) {
    const { data: promos } = await supabase
      .from("promo_codes")
      .select("id, code")
      .in("id", Array.from(new Set(promoIds)));
    for (const row of promos ?? []) {
      promoCodeById.set(row.id as string, row.code as string);
    }
  }

  // Pull the latest final invoice per case so we can render the
  // Final invoice row in the billing tab. One row per case keyed by
  // competition_id, sorted by created_at desc.
  const [finalInvoicesRes, initialInvoicesRes] = await Promise.all([
    supabase
      .from("invoices")
      .select(
        "id, competition_id, status, amount_cents, tax_cents, total_cents, due_date, paid_at, failed_at, voided_at, failure_reason, void_reason, stripe_invoice_hosted_url, stripe_invoice_pdf_url, stripe_receipt_url, created_at",
      )
      .in("competition_id", competitionIds)
      .eq("kind", "final")
      .order("created_at", { ascending: false }),
    supabase
      .from("invoices")
      .select(
        "id, competition_id, status, amount_cents, tax_cents, total_cents, paid_at, failed_at, failure_reason, hosted_invoice_url, receipt_url, stripe_receipt_url, expires_at, created_at",
      )
      .in("competition_id", competitionIds)
      .eq("kind", "initial")
      .order("created_at", { ascending: false }),
  ]);
  const finalInvoices = finalInvoicesRes.data;
  const initialInvoices = initialInvoicesRes.data;
  const latestFinalByCompetition = new Map<string, BillingFinalInvoice>();
  for (const row of (finalInvoices ?? []) as Array<{ competition_id: string } & BillingFinalInvoice>) {
    if (!latestFinalByCompetition.has(row.competition_id)) {
      latestFinalByCompetition.set(row.competition_id, {
        id: row.id,
        status: row.status,
        amount_cents: row.amount_cents,
        tax_cents: row.tax_cents,
        total_cents: row.total_cents,
        due_date: row.due_date,
        paid_at: row.paid_at,
        failed_at: row.failed_at,
        voided_at: row.voided_at,
        failure_reason: row.failure_reason,
        void_reason: row.void_reason,
        stripe_invoice_hosted_url: row.stripe_invoice_hosted_url,
        stripe_invoice_pdf_url: row.stripe_invoice_pdf_url,
        stripe_receipt_url: row.stripe_receipt_url,
      });
    }
  }

  // Same collapse for initial invoices (kind='initial').
  const latestInitialByCompetition = new Map<string, BillingInitialPayment>();
  for (const row of (initialInvoices ?? []) as Array<
    { competition_id: string } & {
      id: string;
      status: BillingInitialPayment["status"];
      amount_cents: number;
      tax_cents: number;
      total_cents: number;
      paid_at: string | null;
      failed_at: string | null;
      failure_reason: string | null;
      hosted_invoice_url: string | null;
      receipt_url: string | null;
      stripe_receipt_url: string | null;
      expires_at: string | null;
    }
  >) {
    if (!latestInitialByCompetition.has(row.competition_id)) {
      latestInitialByCompetition.set(row.competition_id, {
        id: row.id,
        status: row.status,
        amount_cents: row.amount_cents,
        tax_cents: row.tax_cents,
        total_cents: row.total_cents,
        paid_at: row.paid_at,
        failed_at: row.failed_at,
        failure_reason: row.failure_reason,
        // Status-gate: stop offering the link once the session has been
        // consumed. For 'pending' and 'failed' we still surface it (Stripe
        // lets the same URL retry within session lifetime).
        checkout_url:
          row.status === "pending" || row.status === "failed"
            ? row.hosted_invoice_url
            : null,
        receipt_url: row.receipt_url ?? row.stripe_receipt_url,
        expires_at: row.expires_at,
      });
    }
  }

  const latestContractByCompetition = new Map<string, ContractRow>();
  for (const row of ((contractRes.data ?? []) as ContractRow[])) {
    if (!latestContractByCompetition.has(row.competition_id)) {
      latestContractByCompetition.set(row.competition_id, row);
    }
  }

  const cases: BillingCase[] = data.map((c) => {
    const q = latestQuoteByCompetition.get(c.id as string) ?? null;
    const k = latestContractByCompetition.get(c.id as string) ?? null;
    const initial = latestInitialByCompetition.get(c.id as string) ?? null;
    return {
      competition_id: c.id as string,
      case_name: c.name as string,
      status: c.status as CompetitionStatus,
      engagement_model: c.engagement_model as EngagementModel,
      tier: (c.tier as CompetitionTier | null) ?? null,
      contract_signed_at: k?.signed_at ?? null,
      payment_paid_at: initial?.paid_at ?? null,
      quote: q ? quoteToBillingQuote(q, q.applied_promo_code_id ? promoCodeById.get(q.applied_promo_code_id) ?? null : null) : null,
      contract: k
        ? {
            id: k.id,
            docusign_envelope_id: k.docusign_envelope_id,
            status: k.status,
            sent_at: k.sent_at,
            viewed_at: k.viewed_at,
            signed_at: k.signed_at,
            declined_at: k.declined_at,
            declined_reason: k.declined_reason,
            voided_at: k.voided_at,
          }
        : null,
      payment: initial,
      final_invoice: latestFinalByCompetition.get(c.id as string) ?? null,
    };
  });

  cases.sort((a, b) => {
    const pa = STATUS_PRIORITY[a.status] ?? 99;
    const pb = STATUS_PRIORITY[b.status] ?? 99;
    if (pa !== pb) return pa - pb;
    const ta = a.quote?.generated_at ?? "";
    const tb = b.quote?.generated_at ?? "";
    return tb.localeCompare(ta);
  });

  return cases;
}
